import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Send, Search, MessageCircle, Loader2, Plus, ArrowLeft, BadgeCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function getWsUrl() {
  const base = process.env.REACT_APP_BACKEND_URL;
  return base.replace(/^https/, 'wss').replace(/^http/, 'ws');
}

const MessagesPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roomSearch, setRoomSearch] = useState('');
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUser, setTypingUser] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const heartbeatRef = useRef(null);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/rooms`, { headers: getAuthHeaders(), withCredentials: true });
      setRooms(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/`, { headers: getAuthHeaders(), withCredentials: true });
      setUsers(res.data.filter(u => u.user_id !== user?.user_id));
    } catch {}
  }, [getAuthHeaders, user]);

  const fetchMessages = useCallback(async (roomId) => {
    if (!roomId) return;
    try {
      const res = await axios.get(`${API}/chat/rooms/${roomId}/messages`, { headers: getAuthHeaders(), withCredentials: true });
      setMessages(res.data);
    } catch {}
  }, [getAuthHeaders]);

  const fetchOnlineUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/online/list`, { headers: getAuthHeaders(), withCredentials: true });
      setOnlineUsers(res.data);
    } catch {}
  }, [getAuthHeaders]);

  // Heartbeat for online status
  useEffect(() => {
    const sendHeartbeat = async () => {
      try { await axios.post(`${API}/users/me/heartbeat`, {}, { headers: getAuthHeaders(), withCredentials: true }); } catch {}
    };
    sendHeartbeat();
    heartbeatRef.current = setInterval(() => { sendHeartbeat(); fetchOnlineUsers(); }, 30000);
    fetchOnlineUsers();
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current); };
  }, [getAuthHeaders, fetchOnlineUsers]);

  useEffect(() => { fetchRooms(); fetchUsers(); }, [fetchRooms, fetchUsers]);

  // WebSocket connection
  useEffect(() => {
    if (!selectedRoom) return;
    setLoadingMessages(true);
    fetchMessages(selectedRoom.room_id).then(() => setLoadingMessages(false));

    const token = localStorage.getItem('auth_token');
    const wsUrl = `${getWsUrl()}/ws/chat/${selectedRoom.room_id}?token=${token}`;
    let ws;
    
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        if (msg.type === 'typing') {
          if (msg.user_id !== user?.user_id) {
            setTypingUser(msg.is_typing ? msg.user_name : null);
            if (msg.is_typing) {
              setTimeout(() => setTypingUser(null), 3000);
            }
          }
          return;
        }
        setMessages(prev => {
          if (prev.some(m => m.message_id === msg.message_id)) return prev;
          return [...prev, msg];
        });
        fetchRooms();
      };

      ws.onclose = () => {
        pollRef.current = setInterval(() => fetchMessages(selectedRoom.room_id), 3000);
      };

      ws.onerror = () => { ws.close(); };
    } catch {
      pollRef.current = setInterval(() => fetchMessages(selectedRoom.room_id), 3000);
    }

    return () => {
      if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [selectedRoom, fetchMessages, fetchRooms, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSelectRoom = (room) => {
    setSelectedRoom(room);
    setMessages([]);
    setTypingUser(null);
    setShowSidebar(false);
  };

  const handleStartChat = async (userId) => {
    try {
      const res = await axios.post(`${API}/chat/rooms`, { user_id: userId }, { headers: getAuthHeaders(), withCredentials: true });
      setShowNewChatDialog(false);
      setSelectedRoom(res.data);
      setShowSidebar(false);
      fetchRooms();
    } catch { toast.error('Failed to start chat'); }
  };

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: true }));
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false }));
        }
      }, 2000);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedRoom) return;
    const content = newMessage.trim();
    setNewMessage('');

    // Stop typing indicator
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false }));
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ content }));
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/chat/rooms/${selectedRoom.room_id}/messages`, { content }, { headers: getAuthHeaders(), withCredentials: true });
      fetchMessages(selectedRoom.room_id);
      fetchRooms();
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  const isOnline = (userId) => onlineUsers.includes(userId);

  const formatTime = (d) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const formatDate = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d);
    const days = Math.floor(diff / 86400000);
    if (days < 1) return formatTime(d);
    if (days < 7) return `${days}d ago`;
    return new Date(d).toLocaleDateString();
  };

  const getMessageDate = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const filteredUsers = users.filter(u => u.name.toLowerCase().includes(searchQuery.toLowerCase()) || u.email?.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredRooms = rooms.filter(r => {
    if (!roomSearch) return true;
    const name = r.other_user?.name || r.name || '';
    return name.toLowerCase().includes(roomSearch.toLowerCase());
  });

  // Group messages by date
  const groupedMessages = messages.reduce((acc, msg) => {
    const dateKey = getMessageDate(msg.created_at);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(msg);
    return acc;
  }, {});

  const otherUserId = selectedRoom?.other_user?.user_id;
  const otherUserOnline = otherUserId ? isOnline(otherUserId) : false;

  return (
    <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="messages-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-0 sm:px-4 pt-16 sm:pt-20 pb-0 sm:pb-4 h-screen">
        <div className="flex h-[calc(100vh-4rem)] sm:h-[calc(100vh-6rem)] border-0 sm:border border-border sm:rounded-xl overflow-hidden bg-card">
          {/* Sidebar */}
          <div className={`${showSidebar ? 'flex' : 'hidden'} sm:flex w-full sm:w-80 border-r border-border flex-col`}>
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif text-xl font-semibold">Messages</h2>
                <Dialog open={showNewChatDialog} onOpenChange={setShowNewChatDialog}>
                  <DialogTrigger asChild>
                    <Button size="icon" variant="ghost" data-testid="new-chat-btn"><Plus className="h-5 w-5" /></Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle className="font-serif">Start New Chat</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <Input placeholder="Search users..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="input-default" data-testid="search-users-input" />
                      <ScrollArea className="h-64">
                        <div className="space-y-1">
                          {filteredUsers.map(u => (
                            <div key={u.user_id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors" onClick={() => handleStartChat(u.user_id)} data-testid={`start-chat-${u.user_id}`}>
                              <div className="relative">
                                <Avatar className="h-10 w-10"><AvatarImage src={u.picture} /><AvatarFallback>{u.name?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
                                {isOnline(u.user_id) && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-card" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="font-medium truncate">{u.name}</p>
                                  {u.is_verified && <BadgeCheck className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />}
                                </div>
                                <p className="text-sm text-muted-foreground capitalize">{u.role}</p>
                              </div>
                              {isOnline(u.user_id) && <Badge className="bg-green-500/10 text-green-500 text-xs">Online</Badge>}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search conversations..." value={roomSearch} onChange={e => setRoomSearch(e.target.value)} className="input-default pl-9" data-testid="search-rooms-input" />
              </div>
            </div>
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : filteredRooms.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">{roomSearch ? 'No matches' : 'No conversations yet'}</p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {filteredRooms.map(room => {
                    const roomOtherOnline = room.other_user?.user_id ? isOnline(room.other_user.user_id) : false;
                    return (
                      <div key={room.room_id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${selectedRoom?.room_id === room.room_id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/50'}`}
                        onClick={() => handleSelectRoom(room)} data-testid={`room-${room.room_id}`}>
                        <div className="relative flex-shrink-0">
                          <Avatar className="h-11 w-11"><AvatarImage src={room.other_user?.picture} /><AvatarFallback>{room.other_user?.name?.charAt(0)?.toUpperCase() || '?'}</AvatarFallback></Avatar>
                          {roomOtherOnline && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-card" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <p className="font-medium truncate text-sm">{room.other_user?.name || room.name || 'Chat'}</p>
                              {room.other_user?.is_verified && <BadgeCheck className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />}
                            </div>
                            <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">{formatDate(room.last_message_at)}</span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{room.last_message || 'Start a conversation'}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Main Chat Area */}
          <div className={`${!showSidebar || selectedRoom ? 'flex' : 'hidden'} sm:flex flex-1 flex-col`}>
            {selectedRoom ? (
              <>
                <div className="p-3 sm:p-4 border-b border-border flex items-center gap-3">
                  <Button variant="ghost" size="icon" className="sm:hidden flex-shrink-0" onClick={() => { setShowSidebar(true); setSelectedRoom(null); }} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="relative flex-shrink-0">
                    <Avatar className="h-10 w-10"><AvatarImage src={selectedRoom.other_user?.picture} /><AvatarFallback>{selectedRoom.other_user?.name?.charAt(0)?.toUpperCase() || '?'}</AvatarFallback></Avatar>
                    {otherUserOnline && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-card" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium">{selectedRoom.other_user?.name || selectedRoom.name || 'Chat'}</p>
                      {selectedRoom.other_user?.is_verified && <BadgeCheck className="h-4 w-4 text-blue-500" />}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {typingUser ? (
                        <span className="text-primary animate-pulse">{typingUser} is typing...</span>
                      ) : otherUserOnline ? (
                        <span className="text-green-500">Online</span>
                      ) : (
                        'Offline'
                      )}
                    </p>
                  </div>
                </div>
                <ScrollArea className="flex-1 p-4">
                  {loadingMessages ? (
                    <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-8"><p className="text-muted-foreground">No messages yet. Say hello!</p></div>
                  ) : (
                    <div className="space-y-1">
                      {Object.entries(groupedMessages).map(([dateLabel, msgs]) => (
                        <div key={dateLabel}>
                          <div className="flex items-center gap-3 my-4">
                            <div className="flex-1 h-px bg-border" />
                            <span className="text-xs text-muted-foreground bg-card px-2">{dateLabel}</span>
                            <div className="flex-1 h-px bg-border" />
                          </div>
                          {msgs.map(msg => {
                            const isMine = msg.sender_id === user?.user_id;
                            return (
                              <div key={msg.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-2`} data-testid={`msg-${msg.message_id}`}>
                                <div className={`max-w-[75%] ${isMine ? 'chat-bubble-sent' : 'chat-bubble-received'} px-4 py-2.5 shadow-sm`}>
                                  <p className="text-sm leading-relaxed">{msg.content}</p>
                                  <p className={`text-[10px] mt-1 ${isMine ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>{formatTime(msg.created_at)}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>
                {/* Typing indicator */}
                {typingUser && (
                  <div className="px-4 pb-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span>{typingUser} is typing...</span>
                    </div>
                  </div>
                )}
                <div className="p-3 sm:p-4 border-t border-border">
                  <div className="flex gap-2">
                    <Input placeholder="Type a message..." value={newMessage}
                      onChange={e => { setNewMessage(e.target.value); handleTyping(); }}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      className="input-default" data-testid="message-input" />
                    <Button onClick={handleSendMessage} disabled={!newMessage.trim() || sending} className="btn-primary" data-testid="send-message-btn">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">Select a conversation</p>
                  <p className="text-muted-foreground">or start a new one</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
